import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { PatientResponse as Patient } from '@emr/shared';

import { getPatient } from '../api/patients.ts';

/**
 * One patient's demographics, by id — shared across every place the schedule view needs to show
 * who an appointment is for (a list row, a card, the reschedule dialog's read-only patient line).
 *
 * A single query key (`['patient', patientId]`) means TanStack Query's own cache is the dedupe: the
 * same patient appearing twice in one week's schedule is fetched once, not twice, and — since
 * `BACKLOG.md` K5 — a week's *distinct* patients are fetched once each too. `SchedulePage.tsx`
 * batch-prefetches every distinct patient a range's appointments reference via `GET /api/patients`'s
 * new `ids` filter (`patientSearchQuerySchema`, `shared/src/clinicalRequests.ts`) and populates this
 * exact `['patient', id]` cache key for each one — so this hook's own per-row call sites in
 * `AppointmentList.tsx`/`CalendarGrid.tsx` needed no change at all; they simply become cache hits
 * once the batch resolves, falling back to their own individual `GET /api/patients/:patientId`
 * fetch only if the batch hasn't landed yet or failed.
 */
export function usePatient(patientId: string | null): UseQueryResult<Patient> {
  return useQuery({
    queryKey: ['patient', patientId],
    // `enabled: false` leaves TanStack Query never calling `queryFn`, so the `!` below is never
    // exercised against `null` — but the callback's own type still has to satisfy `queryFn`'s
    // signature, which is why the guard is written this way rather than skipped.
    queryFn: ({ signal }) => getPatient(patientId ?? '', signal),
    enabled: patientId !== null,
  });
}
